import axios from 'axios';
import {useContext, useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import UserContext, {AUTH_STATUS} from '../../../context/UserContext';
import useForm from '../../../hooks/useForm';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import Box from '../../shared/layouts/Box';
import Stack from '../../shared/layouts/Stack';
import Loader from '../../shared/Loader';
import Modal from '../../shared/Modal';
import Cluster from '../../shared/layouts/Cluster';
import Labeled from '../../shared/WithLabel';
import Messagebar from '../../shared/Messagebar';
import LoginContainer from './LoginContainer';
import WhiteLabelContext from '../../../context/WhiteLabelContext';
import Errors from "../../shared/Errors";
import {useTranslation} from "react-i18next";
import {sendDataToReactNative} from "../../../utils/communicateWithRN";

const EMAIL_STATUS = {
  unknown: 'UNKOWN',
  confirmd: 'CONFIRMD',
  notConfirmed: 'NOT_CONFIRMD',
  requested: 'REQUESTED',
};

const LoginPage = ({email}) => {
  const navigate = useNavigate();
  const [state, handleState] = useForm({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [emailStatus, setEmailStatus] = useState(EMAIL_STATUS.unknown);
  const { setUser, authStatus, initPathname } = useContext(UserContext);
  const { platformName, advisorWebsiteUrl, canRequestInvite, multipleDossiers, openIdConnectEnabled } = useContext(WhiteLabelContext);
  const { t } = useTranslation();
  const { token } = useParams();

  useEffect(() => {
    sendDataToReactNative('loaded')
  }, [])

  useEffect(() => {
    if (!token) {
      return;
    }
    handleState({target: {name: 'email', value: email}});
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault();
    axios
        .post('/login', {...state, token: token }, {
          headers: { 'Content-Type': 'application/json' },
        })
        .then((res) => {
          const user = res?.data?.user;
          const oauthRequest = res?.data?.oauth;

          if (user) {
            if (oauthRequest.requested) {
              window.location.href = oauthRequest.url;
            }else{
              setUser(res.data.user);
              if (user.mfa_enabled) {
                navigate('/mfa');
              } else if (multipleDossiers && !user.dossier) {
                navigate('/dossier-select');
              } else {
                navigate(initPathname ? initPathname : '/dashboard');
              }
            }
          }
        })
        .catch((err) => {
          if ( typeof err?.response?.data?.email_confirmed !== 'undefined' && !err?.response?.data?.email_confirmed) {
            sendDataToReactNative(JSON.stringify(err.response.data))
            setEmailStatus(EMAIL_STATUS.notConfirmed);
          } else {
            sendDataToReactNative(JSON.stringify(err.response.data.errors))
            setErrors(err.response.data.errors);
          }
        });
  };

  if (authStatus === AUTH_STATUS.loading) return <Loader />;

  return (
      <>
        {emailStatus === EMAIL_STATUS.notConfirmed && (
            <Modal
                isOpen={emailStatus === EMAIL_STATUS.notConfirmed}
                onRequestClose={() => {
                  setEmailStatus(EMAIL_STATUS.unknown);
                }}
                showProgressBar
                progressBarColor="alert"
            >
              <Stack space="l">
                <Box fontScale="xl" fontWeight="bold">
                  {t('E-mailadres nog niet bevestigd')}
                </Box>
                <Box>
                  {t('Bevestig eerst je aanmelding voor {{platformName}} via de mail die je van ons ontving op dit e-mailadres. Ga dan verder.', {platformName})}
                </Box>
              </Stack>
              <Cluster mt="xl" display="flex" justifyContent="flex-end" hGap="m">
                <Button
                    variant="primary-outline"
                    onClick={() => {
                      axios({
                        method: 'POST',
                        url: '/bevestigen/verzenden',
                        data: { email: state.email },
                      })
                          .then(() => {
                            setEmailStatus(EMAIL_STATUS.requested);
                          })
                          .catch((err) => {
                            if (err.response.status === 400) {
                              setEmailStatus(EMAIL_STATUS.confirmed);
                            } else {
                              setErrors(err?.repsonse?.data?.errors);
                            }
                          });
                    }}
                >
                  {t('Verstuur opnieuw')}
                </Button>
                <Button
                    id="goBack"
                    variant="primary"
                    onClick={() => {
                      setEmailStatus(EMAIL_STATUS.unknown);
                    }}
                >
                  {t('Terug')}
                </Button>
              </Cluster>
            </Modal>
        )}
        <Messagebar />
        <LoginContainer handleSubmit={handleSubmit} advisorWebsiteUrl={advisorWebsiteUrl}>
          <Box fontScale="2xl" fontWeight="bold">
            {t('Welkom bij {{platformName}}!', {platformName})}
          </Box>
          <Box>{
            token ?
              t('Om te verbinden met dit dossier dien je eerst in te loggen.')
              :
              t('Log in met je e-mailadres en wachtwoord')
          }</Box>
          <Labeled label={t('E-mailadres')}>
            <Input
                name="email"
                value={state.email}
                onChange={handleState}
                errors={errors?.email}
                placeholder={t('Vul je e-mailadres in')}
                disabled={token}
            />
          </Labeled>
          <Labeled label={t('Wachtwoord')}>
            <Input
                name="password"
                type="password"
                value={state.password}
                onChange={handleState}
                errors={errors?.password}
                placeholder={t('Vul je wachtwoord in')}
            />
          </Labeled>
          <Link
              id="forgotPassword"
              to="/forgot-password"
              css={(theme) => ({
                fontSize: theme.fontSizes.s,
                marginTop: '8px',
                textDecoration: 'underline',
                color: theme.colors.secondaryButtonHover,
                fontWeight: 'bold',
              })}
          >
            {t('Wachtwoord vergeten?')}
          </Link>
          <Button variant="primary" width="100%" type="submit">
            {t('Inloggen')}
          </Button>

          {openIdConnectEnabled && (
            <Button
              width="100%"
              variant="primary-outline"
              onClick={() => {
                window.location.href = '/openid-connect/login'
              }}
            >
              {t('Login met OpenID Connect')}
            </Button>
          )}

          {canRequestInvite && (
              <Button
                  width="100%"
                  variant="primary-outline"
                  onClick={() => {
                      navigate("/request-invite");
                  }}
              >
                  {t('Account aanmaken')}
              </Button>
          )}
          <Errors errors={errors?.not_activated} />
        </LoginContainer>
      </>
  );
};
export default LoginPage;
